import React, { useEffect, useState } from 'react';
import { defaultReportMonth } from '../../utils/dateUtils';
import { Button, TextField, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Budget } from '../../interfaces/accounting.interface';
import { BudgetService } from '../../services/budget.service';
import BudgetForm from './BudgetForm';
import { formatMoney } from '../../utils/format';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
];

const BudgetList: React.FC = () => {
    const canManage = usePermission(PERMISSIONS.BUDGET_MANAGE); // set budget = BUDGET_MANAGE server-side
    const initialPeriod = defaultReportMonth(new Date());
    const toast = useToast();
    const [budgets, setBudgets] = useState<Budget[]>([]);
    const [loading, setLoading] = useState(false);
    const [year, setYear] = useState<number>(initialPeriod.year);
    const [month, setMonth] = useState<number>(initialPeriod.month);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Edit an existing month's budget amount (re-setting the same account/month is rejected as a duplicate).
    const [editing, setEditing] = useState<Budget | null>(null);
    const [editAmount, setEditAmount] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    const openEdit = (budget: Budget) => {
        setEditing(budget);
        setEditAmount(String(budget.budgetAmount ?? ''));
    };

    const handleSaveEdit = async () => {
        if (!editing?.id) return;
        const amount = Number(editAmount);
        if (!Number.isFinite(amount) || amount < 0) {
            toast.showError('Enter a budget amount of zero or more');
            return;
        }
        try {
            setSavingEdit(true);
            await BudgetService.updateBudget(editing.id, { ...editing, budgetAmount: amount });
            toast.showSuccess('Budget updated');
            setEditing(null);
            fetchComparison();
        } catch (err) {
            toast.showApiError(err, 'Failed to update budget');
        } finally {
            setSavingEdit(false);
        }
    };


    const fetchComparison = async () => {
        try {
            setLoading(true);
            const data = await BudgetService.getBudgetComparison(year, month);
            setBudgets(Array.isArray(data) ? data : (data.content || []));
        } catch (err) {
            toast.showApiError(err, 'Failed to load budget comparison');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComparison();
    }, []);

    const handleLoadComparison = () => {
        fetchComparison();
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.budgetList.page">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Budgets</h2>
                {canManage && (
                    <Button data-help="accounting.budgetList.set" variant="contained" color="primary" onClick={handleOpenModal}>
                        Set Budget
                    </Button>
                )}
            </div>

            {/* Year/Month Selectors */}
            <div data-help-section="accounting.budgetList.period" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px', alignItems: 'center' }}>
                <TextField data-help="accounting.budgetList.year"
                    label="Year"
                    type="number"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    size="small"
                    style={{ flex: '1 1 110px', maxWidth: 160 }}
                    inputProps={{ min: 2000, max: 2100 }}
                />
                <FormControl data-help="accounting.budgetList.month" size="small" style={{ flex: '1 1 150px', maxWidth: 220 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                        value={month}
                        label="Month"
                        onChange={(e) => setMonth(Number(e.target.value))}
                    >
                        {MONTHS.map(m => (
                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button data-help="accounting.budgetList.load" variant="outlined" onClick={handleLoadComparison}>
                    Load Comparison
                </Button>
            </div>

            {loading ? (
                <div>Loading...</div>
            ) : (
                <div data-help-section="accounting.budgetList.table">
                <ScrollableTable>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                    <thead>
                        <tr>
                            <th data-help="accounting.budgetList.col.code" style={tableHeaderStyle}>Account Code</th>
                            <th data-help="accounting.budgetList.col.name" style={tableHeaderStyle}>Account Name</th>
                            <th data-help="accounting.budgetList.col.budget" style={tableHeaderStyle}>Budget Amount</th>
                            <th data-help="accounting.budgetList.col.actual" style={tableHeaderStyle}>Actual Amount</th>
                            <th data-help="accounting.budgetList.col.variance" style={tableHeaderStyle}>Variance</th>
                            {canManage && <th data-help="accounting.budgetList.col.actions" style={tableHeaderStyle}>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {budgets.length === 0 ? (
                            <tr>
                                <td style={tableCellStyle} colSpan={canManage ? 6 : 5}>No budget data found for this period</td>
                            </tr>
                        ) : (
                            budgets.map((budget, index) => {
                                const variance = budget.variance !== undefined
                                    ? budget.variance
                                    : (budget.budgetAmount - (budget.actualAmount || 0));
                                return (
                                    <tr key={budget.id || index}>
                                        <td style={tableCellStyle}>{budget.accountCode || '-'}</td>
                                        <td style={tableCellStyle}>{budget.accountName || '-'}</td>
                                        <td style={tableCellStyle}>{formatMoney(budget.budgetAmount)}</td>
                                        <td style={tableCellStyle}>{formatMoney(budget.actualAmount || 0)}</td>
                                        <td style={{
                                            ...tableCellStyle,
                                            color: variance >= 0 ? 'green' : 'red',
                                            fontWeight: 'bold'
                                        }}>
                                            {variance >= 0 ? '+' : ''}{formatMoney(variance)}
                                        </td>
                                        {canManage && (
                                            <td style={tableCellStyle}>
                                                {budget.id && (
                                                    <Button data-help="accounting.budgetList.edit" size="small" onClick={() => openEdit(budget)}>Edit</Button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
                </ScrollableTable>
                </div>
            )}

            <Dialog data-help-page="accounting.budgetList.edit.page" open={editing !== null} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Edit budget</DialogTitle>
                <DialogContent>
                    <div style={{ marginBottom: 12, marginTop: 4 }}>
                        {editing?.accountCode} — {editing?.accountName}
                    </div>
                    <TextField data-help="accounting.budgetList.edit.field.amount"
                        label="Budget amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={editAmount}
                        onChange={e => setEditAmount(e.target.value)}
                        inputProps={{ min: 0, step: '0.01' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.budgetList.edit.cancel" onClick={() => setEditing(null)}>Cancel</Button>
                    <Button data-help="accounting.budgetList.edit.save" variant="contained" onClick={handleSaveEdit} disabled={savingEdit}>
                        {savingEdit ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <BudgetForm
                open={isModalOpen}
                onClose={handleCloseModal}
                onSuccess={fetchComparison}
                initialYear={year}
                initialMonth={month}
            />
        </div>
    );
};

export default BudgetList;
